import React, { useState } from 'react';
import { useGetItemsQuery } from '../store/docApi';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  CircularProgress,
  Modal,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import EventIcon from '@mui/icons-material/Event';
import EventForm from './EventForm';

export default function Events() {
  const { data: events, error, isLoading } = useGetItemsQuery({ label: 'event' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleOpenModal = (event = null) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Failed to load events: {error.message}
      </Typography>
    );
  }

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Events</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Create Event
        </Button>
      </Box>

      {(!events || events.length === 0) ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No events found</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create your first event to get started
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Create Event
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
            mt: 2
          }}
        >
          {events.map((event) => (
            <Card 
              key={event.id} 
              component={Link}
              to={`/events/${event.pk || 'event'}/${event.id}`}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                '&:hover': { boxShadow: 3 },
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              {event.imageUrl ? (
                <CardMedia
                  component="img"
                  height="140"
                  image={event.imageUrl}
                  alt={event.title}
                />
              ) : (
                <Box
                  sx={{
                    height: 140,
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <EventIcon sx={{ fontSize: 60, color: 'grey.500' }} />
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" component="div" noWrap>
                    {event.title}
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenModal(event)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {formatDate(event.startDateTime)}
                </Typography>
                {event.location && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {event.location.name || event.location.address}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, flexGrow: 1 }}>
                  {event.description?.substring(0, 100)}{event.description?.length > 100 ? '...' : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" color="text.secondary">
                    {event.attendeeCount || 0} attending
                  </Typography>
                  <Button 
                    size="small" 
                    color="primary"
                    component={Link}
                    to={`/events/${event.pk || 'event'}/${event.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%', lg: '50%' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          overflowY: 'auto'
        }}>
          <EventForm event={selectedEvent} onClose={handleCloseModal} />
        </Box>
      </Modal>
    </Box>
  );
}
